import { useEffect, useState } from "react";
import { api, extractError } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, MapPin } from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";

const empty = {
  name: "", state: "", is_active: true,
  default_slots: ["08:30", "11:00", "13:30", "15:30"],
  workdays: [0,1,2,3,4],
  large_property_threshold: 130,
  min_lead_hours: 12,
  max_advance_days: 60,
};

const WD = ["Seg","Ter","Qua","Qui","Sex","Sáb","Dom"];

export default function Cities() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/cities").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing(null); setForm(empty); setOpen(true); };
  const openEdit = (c) => { setEditing(c); setForm({ ...empty, ...c }); setOpen(true); };

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...form,
        default_slots: form.default_slots.filter(Boolean),
        large_property_threshold: Number(form.large_property_threshold),
        min_lead_hours: Number(form.min_lead_hours),
        max_advance_days: Number(form.max_advance_days),
      };
      if (editing) await api.patch(`/cities/${editing.id}`, payload);
      else await api.post("/cities", payload);
      toast.success(editing ? "Cidade atualizada" : "Cidade criada");
      setOpen(false); load();
    } catch (e) { toast.error(extractError(e)); }
    finally { setSaving(false); }
  };

  const del = async (c) => {
    if (!window.confirm(`Excluir a cidade ${c.name}/${c.state}?`)) return;
    try { await api.delete(`/cities/${c.id}`); toast.success("Cidade excluída"); load(); }
    catch (e) { toast.error(extractError(e)); }
  };

  const toggleWd = (n) => {
    setForm((f) => ({ ...f, workdays: f.workdays.includes(n) ? f.workdays.filter((x)=>x!==n) : [...f.workdays, n].sort() }));
  };

  const chip = (active) => `px-3 py-1.5 text-xs border rounded transition-colors ${active ? "bg-[#0B1F3A] border-[#0B1F3A] text-[#FFD400] font-semibold" : "bg-white border-[#E5E7EB] text-[#6B7280] hover:border-[#0B1F3A]"}`;

  return (
    <div className="space-y-6" data-testid="cities-page">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="inline-block text-[10px] uppercase tracking-[0.3em] font-bold px-2 py-0.5 bg-[#FFD400] text-[#0B1F3A] rounded-sm">Configuração</div>
          <h1 className="font-display text-3xl sm:text-4xl font-black tracking-tighter mt-2 text-[#0B1F3A]">Cidades</h1>
          <p className="text-sm text-[#6B7280] mt-1">Ative, edite e configure as cidades onde a Prumo opera.</p>
        </div>
        <Button onClick={openNew} className="bg-[#0B1F3A] hover:bg-[#081A32] text-white" data-testid="cities-new-btn">
          <Plus size={16} className="mr-2" /> Nova cidade
        </Button>
      </div>

      <div className="border border-[#E5E7EB] bg-white rounded-md overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-[10px] uppercase tracking-[0.2em] text-[#6B7280] border-b border-[#E5E7EB] bg-[#F9FAFB]">
              <th className="px-4 py-3 font-bold">Cidade</th>
              <th className="px-4 py-3 font-bold">UF</th>
              <th className="px-4 py-3 font-bold">Status</th>
              <th className="px-4 py-3 font-bold">Horários padrão</th>
              <th className="px-4 py-3 font-bold text-right">Limite m²</th>
              <th className="px-4 py-3 font-bold text-right">Ações</th>
            </tr>
          </thead>
          <tbody>
            {items.map((c) => (
              <tr key={c.id} className="border-b border-[#F1F2F4] hover:bg-[#F9FAFB]" data-testid={`city-row-${c.id}`}>
                <td className="px-4 py-3 flex items-center gap-2 text-[#0A0A0A] font-medium"><MapPin size={14} className="text-[#0B1F3A]" />{c.name}</td>
                <td className="px-4 py-3 font-mono-data text-[#0A0A0A]">{c.state}</td>
                <td className="px-4 py-3">
                  <span className={`text-[10px] uppercase tracking-widest font-bold px-2 py-0.5 rounded-sm ${c.is_active ? "bg-emerald-50 text-emerald-700" : "bg-neutral-100 text-neutral-500"}`}>
                    {c.is_active ? "Ativa" : "Inativa"}
                  </span>
                </td>
                <td className="px-4 py-3 font-mono-data text-xs text-[#6B7280]">{(c.default_slots || []).join(" · ")}</td>
                <td className="px-4 py-3 text-right font-mono-data text-[#0A0A0A]">{c.large_property_threshold} m²</td>
                <td className="px-4 py-3 text-right">
                  <button onClick={() => openEdit(c)} className="text-[#6B7280] hover:text-[#0B1F3A] mr-3" data-testid={`city-edit-${c.id}`}><Pencil size={14} /></button>
                  <button onClick={() => del(c)} className="text-[#6B7280] hover:text-red-600" data-testid={`city-delete-${c.id}`}><Trash2 size={14} /></button>
                </td>
              </tr>
            ))}
            {items.length === 0 && (
              <tr><td colSpan={6} className="px-4 py-8 text-center text-[#9CA3AF] text-sm">Nenhuma cidade cadastrada.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-white border-[#E5E7EB] text-[#0A0A0A] max-w-2xl">
          <DialogHeader>
            <DialogTitle className="font-display text-[#0B1F3A]">{editing ? "Editar cidade" : "Nova cidade"}</DialogTitle>
            <DialogDescription className="text-[#6B7280]">Configure horários, dias e limites operacionais desta cidade.</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <Label>Nome</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="city-form-name" />
            </div>
            <div>
              <Label>UF</Label>
              <Input value={form.state} maxLength={2} onChange={(e) => setForm({ ...form, state: e.target.value.toUpperCase() })} data-testid="city-form-state" />
            </div>
            <div className="sm:col-span-2">
              <Label>Horários padrão (separe por vírgula)</Label>
              <Input
                value={(form.default_slots || []).join(", ")}
                onChange={(e) => setForm({ ...form, default_slots: e.target.value.split(",").map((s) => s.trim()) })}
                data-testid="city-form-slots"
              />
            </div>
            <div className="sm:col-span-2">
              <Label>Dias de funcionamento</Label>
              <div className="flex gap-2 mt-2 flex-wrap">
                {WD.map((d, i) => (
                  <button
                    type="button" key={d} onClick={() => toggleWd(i)}
                    className={chip(form.workdays.includes(i))}
                    data-testid={`city-workday-${i}`}
                  >{d}</button>
                ))}
              </div>
            </div>
            <div>
              <Label>Limite m² (2 horários)</Label>
              <Input type="number" value={form.large_property_threshold} onChange={(e) => setForm({ ...form, large_property_threshold: e.target.value })} data-testid="city-form-threshold" />
            </div>
            <div>
              <Label>Prazo mínimo (horas)</Label>
              <Input type="number" value={form.min_lead_hours} onChange={(e) => setForm({ ...form, min_lead_hours: e.target.value })} />
            </div>
            <div>
              <Label>Antecedência máxima (dias)</Label>
              <Input type="number" value={form.max_advance_days} onChange={(e) => setForm({ ...form, max_advance_days: e.target.value })} />
            </div>
            <div className="flex items-center gap-3 pt-6">
              <Switch checked={form.is_active} onCheckedChange={(v) => setForm({ ...form, is_active: v })} data-testid="city-form-active" />
              <Label>Cidade ativa</Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} className="border-[#E5E7EB] text-[#0A0A0A] hover:bg-[#F5F6F8]">Cancelar</Button>
            <Button onClick={save} disabled={saving} className="bg-[#0B1F3A] hover:bg-[#081A32] text-white" data-testid="city-form-save">{saving ? "Salvando…" : "Salvar"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
